import React, { useEffect, useState } from 'react';
import IllegalMoveForm, { IllegalMoveFormData } from './IllegalMoveForm';
import SkillStamps from './SkillStamps';
import IllegalMoveCommentPopUp from './IllegalMoveCommentPopUp';
import { useNavigate } from "react-router-dom";
import { RemoveCircleOutlineTwoTone } from '@mui/icons-material';

interface JudgeScore {
  Judge: string;
  Technique: number;
  Execution: number;
  Choreography: number;
  OverallEffect: number;
  Difficulty: number;
  Max_Difficulty: number;
  PointsDeduction: number;
  Max_Technique: number;
  Max_Execution: number;
  Max_Choreography: number;
  Max_OverallEffect: number;
  Max_PointsDeduction: number;
  isHighest: number;
  isLowest: number;
  Total: number;
  PointsDeduction_Comment: string;
  user_judge_id:number;
}

interface Result {
  entry_number: number;
  performers: {
    id: number;
    first_name: string;
    last_name: string;
  }| {
    id: number;
    first_name: string;
    last_name: string;
  }[];
  program: string;
  program_skills_abhs: number;
  program_skills_abhs_mod2: number;
  program_skills_pom_straps: number;
  routine: string;
  totalscore: number;
  finalscore: number;
  judges: JudgeScore[];
  performance_id: number;
  event_id:number;
  user_programdirector_id: number;
  is_editing_allowed: boolean;
  performance_type: string;
}

interface ResultsCardProps {
  result: Result;
  currentRole: number;
  divisionStatus: string;
  onIllegalMoveSubmit: (formData: IllegalMoveFormData) => void;
  onRemoveIllegalMovePoints: (formData: IllegalMoveFormData) => void;
  removeJudgeScores: (event_id:number, performance_id:number, judge_id:number, entry_number: number) => void;
  performanceAction: (performance_id:number, entry_number:number, is_editing_allowed:boolean, judges_count:number) => void;
  closePopup?: (closeFn: () => void) => void;
}

const ResultsCard: React.FC<ResultsCardProps> = ({ currentRole, result, divisionStatus, onIllegalMoveSubmit, onRemoveIllegalMovePoints, removeJudgeScores, performanceAction, closePopup = () => {}, }) => {
  // State to manage whether the judge details are expanded or collapsed
  const [isExpanded, setIsExpanded] = useState(false);

  // Toggle the expansion state when the header is clicked
  const toggleExpansion = () => {
    //console.log(result.judges[0].name);
    setIsExpanded(prevState => !prevState);
  };

  const [isIllegalMoveFormDataOpen, setIllegalMoveFormDataOpen] = useState<boolean>(false);
  const [isIllegalMoveCommentPopUpOpen, setIllegalMoveCommentPopUpOpen] = useState<boolean>(false);

  const handleOpenIllegalMoveForm = () => {
    setIllegalMoveFormDataOpen(true);
  };

  const handleShowComment = () => {
    setIllegalMoveCommentPopUpOpen(true);
    //alert("comment..");
  };
  const handleCloseComment = () => {
    setIllegalMoveCommentPopUpOpen(false);
  };

  const handleIllegalMoveRemove = () => {
    const removeIllegalMovePoints: IllegalMoveFormData = {performance_id: result.performance_id, entry_number: result.entry_number, comment: ""};
    onRemoveIllegalMovePoints(removeIllegalMovePoints);
  };

  const handleCloseIllegalMoveForm = () => {
    //console.log("close popup calling handleCloseIllegalMoveForm.");
    setIllegalMoveFormDataOpen(false);
  };

  useEffect(() => {
    closePopup(() => {
      //console.log("closePopup called but no specific logic needed.");
    });
      setIllegalMoveFormDataOpen(false);
    
  }, [closePopup]);
  
  const navigate = useNavigate();

  const userData = JSON.parse(localStorage.getItem("user") || "{}") as {
    modules?: { 
      title: string; ModulesPermissions?: { view?: boolean, delete?: boolean, update?: boolean }
    }[];
  };
  const currentUserID = JSON.parse(localStorage.getItem("user") || "{}").id;
  const scorecardModule = userData?.modules?.find(module => module.title === "Scorecard" );
  const canViewScorecard = scorecardModule?.ModulesPermissions?.view;
  const canDeleteScorecard = !!scorecardModule?.ModulesPermissions?.delete;
  const canEditScorecard = !!scorecardModule?.ModulesPermissions?.update;

  const renderJudgeScores = (judge: JudgeScore, categories: string[]) => {
    return categories.map((category) => {
      const value = judge[category as keyof JudgeScore];
      const isStrikethrough = judge.isHighest || judge.isLowest;
      return (
        <div key={category} className="judge-result-list">
          {category === 'PointsDeduction' ? (
            value ? (
              <h5
                className={`performance-list-text ${isStrikethrough ? 'strikethrough' : ''}`}
                style={{ color: 'red' }}
              >
                -{value}
              </h5>
            ) : (
              <h5 className="performance-list-text">&nbsp;</h5> // Render an empty placeholder
            )
          ) : (
            <h5
              className={`performance-list-text ${isStrikethrough ? 'strikethrough' : ''}`}
            >
              {`${value}${category !== 'Total' ? `/${judge[`Max_${category}` as keyof JudgeScore]}` : '/100'}`}
            </h5>
          )}
        </div>
      );
    });
  };
  
  const renderEditDeleteActions = (judge: JudgeScore) => {
    return (
      <div className="judge-result-list">
        <h5 className="performance-list-text">
          {canEditScorecard && (
            <>
              <a href="#" onClick={() => {
                navigate(`/scorecard/${result.performance_id}`, {
                  state: { judge_id: judge?.user_judge_id }
                });
              }}>Edit</a>&nbsp;&nbsp;|&nbsp;&nbsp;
            </>
          )}
          {canDeleteScorecard && (
            <>
              <a href="#" onClick={() =>
                removeJudgeScores(result?.event_id, result?.performance_id, judge?.user_judge_id, result?.entry_number)
              }>Delete</a>
            </>
          )}
        </h5>
      </div>
    );
  };
  
  const renderViewAction = (judge: JudgeScore) => {
    return (
      <div className="judge-result-list">
        <h5 className="performance-list-text">
          <a href="#" onClick={() => {
            navigate(`/scorecard/${result.performance_id}`, {
              state: { judge_id: judge?.user_judge_id }
            });
          }}>
            View
          </a>
        </h5>
      </div>
    );
  };

const categories = result.performance_type === 'sds' 
? ['Technique', 'Execution', 'Choreography', 'OverallEffect', 'PointsDeduction', 'Total'] 
: ['Technique', 'Execution', 'Difficulty', 'OverallEffect', 'PointsDeduction', 'Total'];
  
const headers = result.performance_type === 'sds' 
? ['Technique', 'Execution', 'Choreography', 'Overall', 'Illegal Moves', 'Total'] 
: ['Technique', 'Execution', 'Difficulty', 'Overall', 'Illegal Moves', 'Total'];

  return (
    <div className="carousel">
      <div className="list" onClick={toggleExpansion} style={{ alignItems: 'normal'}}>
        <div className="entry-number">
          <h5 className="performance-list-text">{result.entry_number}</h5>
        </div>
        <div className="program-or-performer">
          <h5 className="performance-list-text">
            <>
              {result.performance_type === 'twd' ? (
                result.program
              ) : Array.isArray(result.performers) ? (
                result.performers.map((p, index) => (
                  <React.Fragment key={index}>
                    {p.first_name} {p.last_name}
                    {index < (result.performers as any[]).length - 1 && <br />}
                  </React.Fragment>
                ))
              ) : (
                `${result.performers?.first_name ?? ""} ${result.performers?.last_name ?? ""}.trim()`
              )}
              <SkillStamps result={result} />
            </>
        </h5>
        </div>
        <div className="routine-name">
          <h5 className="performance-list-text">{result.routine}</h5>
        </div>
        <div className="total-score">
          <h5 className="performance-list-text">
          {result.judges && result.judges[0]?.Judge && (
            <div>
              <strong>{result.finalscore}</strong>
            </div>
          )}
          </h5>
        </div>

        <div className="dropdown-arrow">
        <img
            src="images/down-arrow-icon.png"
            loading="lazy"
            alt="arrow icon"
            className={`image-6 ${isExpanded ? "flipped" : ""}`}
        />
        </div>

        <div>
        {result.judges.length > 0 && (
          <div style={{display: "flex", width: "80px" }}>    
            {(currentRole === 1 || currentRole === 8) && divisionStatus === "judging" && (
              <h5 className="performance-list-text linktext" 
                style={{margin: "0 5px", color:`${result?.is_editing_allowed ?'green':'red'}`}}
                onClick={(e) => {
                e.stopPropagation(); // Prevent toggleExpansion from being triggered
                performanceAction(result?.performance_id,result?.entry_number, !result?.is_editing_allowed, result.judges.length);
              }}>
                {((result?.is_editing_allowed) ? 'Approve' : 'Disapprove')}
              </h5>
            )}
            {currentRole === 7 && !result?.is_editing_allowed && (
              <h5 className="performance-list-text linktext" 
                style={{margin: "0 5px", color:"green"}}
                >
                Approved
              </h5>
            )}
          </div>
        )}
        </div>

        <div>
        {result.judges.length > 0 && (
          <div style={{display: "flex"}}>
            {(currentRole === 1 || currentRole === 8) && divisionStatus === "judging" && (
              <h5 className="performance-list-text linktext" onClick={(e) => {
                e.stopPropagation(); // Prevent toggleExpansion from being triggered
                handleOpenIllegalMoveForm();
              }}> 
                Illegal Moves
              </h5>
            )}
          </div>
        )}
        </div>
        <div>
        {result.judges.length > 0 && (
          <div style={{display: "flex"}}>
            
            {result?.judges[0]?.PointsDeduction && (
              <div>
              <img src="images/IllegalMoveIcon.png" loading="lazy" alt="" className="image-7" 
              onClick={(e) => {
                e.stopPropagation(); // Prevent toggleExpansion from being triggered
                handleShowComment();
              }}
              />
                &nbsp;
                {(currentRole === 1 || currentRole === 8) && divisionStatus === "judging" && (
                  <RemoveCircleOutlineTwoTone
                    sx={{ fontSize: "19px", transform: 'translateY(5px)', color: "red" }}
                    onClick={(e) => { 
                      e.stopPropagation(); // Prevent triggering the row click event
                      handleIllegalMoveRemove(); // Call the delete function when the icon is clicked
                    }}
                  />
                )}
              </div>
            )}
          </div>
        )}
        </div>

      </div>

      {/* Conditionally render the expanded section with judge results */}
      {isExpanded && (
      <>
        {result.performance_type && (
          <div className="drop-down-list-new active">
            <div className="judge-result">
              <div className="judges-name">
                <h5 className="performance-list-text"><strong>Judge</strong></h5>
              </div>
              
                {/* Render Headers */}
                {headers.map((header, index) => (
                  <div key={index} className="judge-result-list">
                    <h5 className="performance-list-text"><strong>{header}</strong></h5>
                  </div>
                ))}
                
            </div>

                  {result.judges.map((judge, index) => (
                    <div key={index} className="judge-result">
                      <div className="judges-name">
                        <h5 className="performance-list-text">{judge.Judge}</h5>
                      </div>
                      {renderJudgeScores(judge, categories)}
                      {(divisionStatus === "judging" && Number(result?.is_editing_allowed) === 1) && renderEditDeleteActions(judge)}
                      {((canViewScorecard && (currentRole===3 ? ((currentUserID === result?.user_programdirector_id) ? true : false ) : true)) && (divisionStatus !== "judging" || (divisionStatus && Number(result?.is_editing_allowed) === 0))) && renderViewAction(judge)}
                    </div>
                  ))}

            </div>
            )}
          </>
        )}

        <IllegalMoveForm
          isOpen={isIllegalMoveFormDataOpen}
          onSubmit={onIllegalMoveSubmit}
          onClose={handleCloseIllegalMoveForm}
          performance_id={result.performance_id}
          entry_number={result.entry_number}
          show_comment={result?.judges[0]?.PointsDeduction_Comment}
         />
        <IllegalMoveCommentPopUp
        isOpen={isIllegalMoveCommentPopUpOpen}
        onClose={handleCloseComment}
        show_comment={result?.judges[0]?.PointsDeduction_Comment}
         /> 
    </div>
  );
};

export default ResultsCard;